/**
 * Cycles the trailing phrase of the hero headline.
 *
 * The markup ships every phrase, with the first one already active, so the
 * headline reads correctly before this runs and still reads correctly if it
 * never does. Assistive tech gets a plain-text copy of the first phrase and
 * skips this element, so the heading does not change under a screen reader.
 */
class HeroRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-rotator-item]'));
    if (this.items.length < 2) return;

    this.index = 0;
    this.interval = Number(this.dataset.interval) || 2750;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = () => this.classList.toggle('is-static', this.motionQuery.matches);
    this.onMotionChange();
    this.motionQuery.addEventListener('change', this.onMotionChange);

    // Nothing is visible on a background tab; cycling there just burns battery.
    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
  }

  start() {
    this.stop();
    this.timer = window.setInterval(this.advance.bind(this), this.interval);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    const current = this.items[this.index];
    this.index = (this.index + 1) % this.items.length;
    const next = this.items[this.index];

    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    next.classList.add('is-active');

    // Clear the outgoing state once its transition has had time to finish.
    window.setTimeout(() => current.classList.remove('is-leaving'), 600);
  }
}

if (!customElements.get('hero-rotator')) {
  customElements.define('hero-rotator', HeroRotator);
}
